import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";
import { colors } from "../styles/colors";

export default function NonCompeteClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <header 
        className="px-6 md:px-12 lg:px-24 py-6" 
        style={{ borderBottom: `1px solid ${colors.border}` }}
      >
        <Link 
          to="/" 
          data-testid="home-link" 
          className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/clauseclear-logo.png" 
            alt="ClauseClear" 
            className="h-10 object-contain"
          />
        </Link>
      </header>

      {/* Hero Banner - subtle dark gradient */}
      <div 
        className="h-32 md:h-48 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)`
        }}
      >
        {/* Decorative ring motif */}
        <div 
          className="absolute top-1/2 right-1/4 w-64 h-64 rounded-full opacity-[0.03]"
          style={{ 
            border: `1px solid ${colors.accent}`,
            transform: 'translateY(-50%)'
          }}
        />
      </div>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link 
              to="/clauses/employment" 
              className="text-sm hover:opacity-80 transition-opacity"
              style={{ color: colors.accent }}
            >
              ← Employment Clauses
            </Link>
          </div>

          <h1 
            data-testid="page-title" 
            className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] mb-8" 
            style={{ color: colors.text }}
          >
            What Does a Non-Compete Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A non-compete clause restricts your ability to work for competitors or start a competing business for a specified period after your employment ends.
              </p>
            </section>

            <section 
              className="mb-12 rounded-2xl p-8" 
              style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
            >
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div 
                className="p-6 rounded-xl text-base" 
                style={{ backgroundColor: colors.backgroundDark, color: colors.textMuted, fontFamily: 'monospace' }}
              >
                "Employee agrees that for a period of twelve (12) months following termination of employment, Employee shall not directly or indirectly engage in any business that competes with the Company within a 50-mile radius of any Company location, nor solicit any clients or customers of the Company."
              </div>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
                <h2 className="text-2xl font-medium" style={{ color: colors.text }}>Plain-English Explanation</h2>
              </div>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                This type of clause limits where you can work and what you can do after leaving a job. It typically includes:
              </p>
              <ul className="space-y-3 ml-6">
                {[
                  'A time restriction (commonly 3-12 months)',
                  'A geographic boundary (city, region, or distance radius)',
                  'Prohibited activities (working for competitors, starting similar business)',
                  'Customer or client contact restrictions'
                ].map((item, i) => (
                  <li key={i} className="text-base md:text-lg leading-relaxed flex items-start gap-3" style={{ color: colors.textMuted }}>
                    <span style={{ color: colors.accent }}>•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
                <h2 className="text-2xl font-medium" style={{ color: colors.text }}>Notable Wording</h2>
              </div>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.text }}>"Directly or indirectly"</strong> - means you can't compete through another company or as a consultant
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.text }}>"Competing business"</strong> - definition may be broad or narrow depending on wording
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.text }}>"Solicit"</strong> - typically means actively approaching, not just responding to inquiries
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
                <h2 className="text-2xl font-medium" style={{ color: colors.text }}>Common Purpose</h2>
              </div>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Employers use non-compete clauses to protect business interests, prevent loss of trade secrets, retain competitive advantage, and discourage employees from taking client relationships to competitors. The restrictions aim to give the company time to replace the employee and secure existing relationships.
              </p>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
                <h2 className="text-2xl font-medium" style={{ color: colors.text }}>Typical Effects</h2>
              </div>
              <ul className="space-y-3 ml-6">
                {[
                  'Limits your career options for the specified period',
                  'May affect your ability to earn income in your field',
                  'Can be legally challenged if overly broad or unreasonable',
                  'Enforcement varies significantly by jurisdiction'
                ].map((item, i) => (
                  <li key={i} className="text-base md:text-lg leading-relaxed flex items-start gap-3" style={{ color: colors.textMuted }}>
                    <span style={{ color: colors.accent }}>•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section 
              className="mb-12 rounded-2xl p-8" 
              style={{ backgroundColor: colors.info, border: `1px solid ${colors.infoBorder}` }}
            >
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.text }}>
                Non-compete clauses are heavily regulated and some jurisdictions limit or prohibit them entirely. Courts often evaluate whether restrictions are reasonable in scope, duration, and geography. Overly broad non-competes may be unenforceable. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-6 h-px" style={{ backgroundColor: colors.accent }} />
                <h2 className="text-2xl font-medium" style={{ color: colors.text }}>Related Clauses</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link 
                  to="/explain-restraint-of-trade-clause" 
                  data-testid="related-link-restraint" 
                  className="p-6 rounded-xl transition-all"
                  style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
                >
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Restraint of Trade</h3>
                  <p className="text-sm" style={{ color: colors.textDim }}>Understand broader business restrictions</p>
                </Link>
                <Link 
                  to="/explain-confidentiality-clause" 
                  data-testid="related-link-confidentiality" 
                  className="p-6 rounded-xl transition-all"
                  style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
                >
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Confidentiality Clause</h3>
                  <p className="text-sm" style={{ color: colors.textDim }}>Learn about information protection</p>
                </Link>
              </div>
            </section>

            <div 
              data-testid="cta-section" 
              className="text-center p-8 rounded-2xl" 
              style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
            >
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link 
                to="/" 
                data-testid="cta-button" 
                className="inline-flex items-center gap-2 hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all"
                style={{ backgroundColor: colors.accent, color: colors.background }}
              >
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>

      <footer 
        className="px-6 md:px-12 lg:px-24 py-12 text-center" 
        style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.backgroundDark }}
      >
        <p className="text-sm mb-2" style={{ color: colors.textDim }}>
          ClauseClear is an educational tool. It does not provide legal advice.
        </p>
        <a 
          href="https://buymeacoffee.com/jamesmoneyz" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-base font-medium hover:opacity-80 transition-opacity" 
          style={{ color: colors.accent }}
        >
          Support the project
        </a>
      </footer>

      <ScrollToTop />
    </div>
  );
}
